import { colors } from "../styles/colors";
import { Link } from "react-router-dom";
import { ArrowRight, Home } from "lucide-react";
import SponsorPlaceholder from "../components/SponsorPlaceholder";
import ScrollToTop from "../components/ScrollToTop";

export default function FixedTermEmployment() {
  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
      {/* Header */}
      <header className="px-6 md:px-12 lg:px-24 py-6 " style={{ borderColor: colors.border }}>
        <Link 
          to="/" 
          data-testid="home-link"
          className="inline-flex items-center gap-2 text-lg font-medium hover:opacity-80 transition-opacity"
          style={{ color: colors.accent }}
        >
          <img src="/clauseclear-logo.png" alt="ClauseClear" className="h-10 object-contain" />

        </Link>
      </header>

      {/* Hero Image */}
      <div 
        className="h-64 md:h-96 bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1506880018603-83d5b814b5a6?crop=entropy&cs=srgb&fm=jpg&q=85)'
        }}
      />

      {/* Content */}
      <article className="px-6 md:px-12 lg:px-24 py-16">
        <div className="max-w-4xl mx-auto">
          <h1 
            data-testid="page-title"
            className="text-4xl md:text-5xl font-light tracking-tight leading-[1.1] mb-8"
            style={{ color: colors.text }}
          >
            What Does a Fixed Term Employment Clause Mean?
          </h1>

          <div className="prose prose-lg max-w-none">
            <section className="mb-12">
              <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>
                Plain-English Explanation
              </h2>
              <p className="text-base md:text-lg leading-relaxed mb-4" style={{ color: colors.textMuted }}>
                A fixed term employment clause sets a specific end date or duration for your job. Instead of continuing indefinitely (like most permanent jobs), your employment automatically ends on a certain date or after a set period, unless both you and your employer agree to extend it.
              </p>
              <p className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                This type of clause is common in project-based work, seasonal roles, maternity cover, or situations where the employer needs someone for a limited time. It creates certainty about when the job will end, but it also means there's typically no obligation to continue employment beyond that date.
              </p>
            </section>

            <section className="mb-12 rounded-2xl p-8" style={{ backgroundColor: colors.backgroundLight, border: `1px solid ${colors.border}` }} style={{ borderColor: colors.border }}>
              <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>
                Example Clause
              </h2>
              <div 
                className="p-6 rounded-xl text-base"
                style={{ backgroundColor: colors.backgroundDark, color: colors.text, fontFamily: 'monospace' }}
              >
                "This employment agreement shall commence on January 1, 2025 and terminate on December 31, 2025, unless extended by mutual written agreement of both parties. Upon the termination date, this agreement shall automatically expire without further notice or action required by either party."
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>
                Why This Clause Is Used
              </h2>
              <p className="text-base md:text-lg leading-relaxed mb-4" style={{ color: colors.textMuted }}>
                Employers use fixed term clauses when they need flexibility or have a specific, time-limited need:
              </p>
              <ul className="space-y-3 ml-6">
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  To staff a temporary project with a known end date
                </li>
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  To cover for someone on leave (maternity, sabbatical, etc.)
                </li>
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  To handle seasonal workload increases
                </li>
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  To assess someone before offering permanent employment
                </li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>
                What This Usually Affects
              </h2>
              <ul className="space-y-3 ml-6">
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  <strong>Job security:</strong> Your role has a clear end date
                </li>
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  <strong>Termination process:</strong> The employer generally doesn't need to provide notice or reasons when the term ends
                </li>
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  <strong>Benefits:</strong> Some benefits might differ from permanent employees
                </li>
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  <strong>Renewal expectations:</strong> There's typically no automatic right to renewal
                </li>
              </ul>
            </section>

            <section className="mb-12 rounded-2xl p-8" style={{ backgroundColor: colors.backgroundLight, border: `1px solid ${colors.border}` }} style={{ borderColor: colors.border }}>
              <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>
                Important Context
              </h2>
              <p className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                Employment law varies by jurisdiction. In some places, repeatedly renewing fixed term contracts can create obligations similar to permanent employment. Additionally, discrimination laws still apply—employers can't use fixed term contracts to avoid providing equal treatment. This explanation covers typical contract language but doesn't constitute legal advice about your specific situation.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-medium mb-6" style={{ color: colors.accent }}>
                Related Clauses
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Link 
                  to="/explain-termination-for-cause-clause"
                  data-testid="related-link-termination"
                  className="p-6 rounded-xl hover:shadow-md transition-all"
                  style={{ borderColor: colors.border }}
                >
                  <h3 className="font-medium mb-2" style={{ color: colors.accent }}>
                    Termination for Cause
                  </h3>
                  <p className="text-sm" style={{ color: colors.textMuted }}>
                    Understand when employers can end employment immediately
                  </p>
                </Link>
                <Link 
                  to="/explain-notice-period-clause"
                  data-testid="related-link-notice"
                  className="p-6 rounded-xl hover:shadow-md transition-all"
                  style={{ borderColor: colors.border }}
                >
                  <h3 className="font-medium mb-2" style={{ color: colors.accent }}>
                    Notice Period
                  </h3>
                  <p className="text-sm" style={{ color: colors.textMuted }}>
                    Learn about advance warning requirements
                  </p>
                </Link>
              </div>
            </section>

            <div 
              data-testid="cta-section"
              className="text-center p-8 rounded-2xl"
              style={{ borderColor: colors.border }}
            >
              <p className="text-lg mb-4" style={{ color: colors.textMuted }}>
                Want to understand your own clause?
              </p>
              <Link
                to="/"
                data-testid="cta-button"
                className="inline-flex items-center gap-2 text-white hover:opacity-90 rounded-full px-8 py-4 text-lg font-medium transition-all"
                style={{ backgroundColor: '#2F3E30' }}
              >
                Try ClauseClear
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>

            {/* Sponsorship Placeholder */}
            <SponsorPlaceholder variant="full" />
          </div>
        </div>
      </article>
    </div>
  );
}
